import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { get } from '@/api/client';
import { Input } from '@/components/atoms/Input';
import { cn } from '@/lib/cn';
import {
  mockBasic,
  formatTickerPrice,
  marketBadgeClass,
  changeColorClass,
  type TickerBasicSummary,
} from '@/lib/tickerMock';

export interface TickerMeta {
  code: string;
  name: string;
  nameEn?: string;
  assetType: 'stock' | 'coin' | 'us_stock';
  market: string;
  /**
   * [mock] 자동완성 요약 필드 — 서버 `GET /tickers/search` 응답에 추후 `basic` 필드로 붙을 예정.
   * 현재는 `mockBasic(meta)` 로 프론트에서 합성한다.
   */
  basic?: TickerBasicSummary;
}

/* `TickerBasicSummary` / `mockBasic` / `formatTickerPrice` / `marketBadgeClass` /
 * `changeColorClass` 는 `@/lib/tickerMock` 에서 import — SearchPage 와 공유 */

interface TickerAutocompleteProps {
  /** 현재 선택된 종목 코드 (예: "005930", "KRW-BTC", "AAPL") */
  value: string;
  /** 선택된 종목의 표시 라벨 (예: "삼성전자 (005930)") */
  displayLabel?: string;
  /** 선택 시 콜백 — 빈 문자열 + undefined 는 "해제" */
  onChange: (code: string, meta?: TickerMeta) => void;
  /** 검색 결과 필터 */
  assetType?: 'stock' | 'coin' | 'us_stock';
  placeholder?: string;
}

/**
 * 종목 자동완성 입력
 * - 종목명/코드/영문명 부분 일치 검색 (GET /tickers/search)
 * - 검색어 없을 때 인기 종목 표시 (GET /tickers/popular)
 * - 선택 후에는 "삼성전자 (005930)" 형태의 칩으로 표시, [X] 로 해제
 * - 외부 클릭 시 드롭다운 자동 닫힘
 * - 각 결과 항목 하단에 요약(현재가/등락률/PER/시총) 노출 — 서버 basic 필드 오기 전까지는 mock
 */
export function TickerAutocomplete({
  value,
  displayLabel,
  onChange,
  assetType,
  placeholder = '종목명 또는 코드 검색 (예: 삼성, 005930, BTC, 비트코인)',
}: TickerAutocompleteProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  /* 200ms debounce */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  /* 외부 클릭 감지 */
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* 검색 쿼리 — 검색어 있으면 search, 없으면 popular */
  const { data } = useQuery({
    queryKey: ['tickers', debouncedQuery || 'popular', assetType ?? 'all'],
    queryFn: () => {
      const params = new URLSearchParams();
      if (assetType) params.set('assetType', assetType);
      if (debouncedQuery) {
        params.set('q', debouncedQuery);
        return get<{ results: TickerMeta[] }>(`/tickers/search?${params.toString()}`);
      }
      params.set('limit', '10');
      return get<{ results: TickerMeta[] }>(`/tickers/popular?${params.toString()}`);
    },
    enabled: isOpen,
    staleTime: 60_000,
  });

  const results = data?.results ?? [];

  const handleSelect = (t: TickerMeta) => {
    onChange(t.code, t);
    setQuery('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('', undefined);
    setQuery('');
    setIsOpen(true); // 해제 후 바로 새로 선택할 수 있도록 드롭다운 오픈
  };

  /* 선택된 상태 + 드롭다운 닫힘 → 칩 표시 */
  if (value && displayLabel && !isOpen) {
    return (
      <div ref={wrapperRef} className="relative">
        <div
          onClick={() => setIsOpen(true)}
          className="flex h-10 cursor-pointer items-center justify-between rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm hover:border-[var(--color-primary)]/40"
        >
          <span className="font-medium">{displayLabel}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
            aria-label="종목 선택 해제"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  /* 검색 입력 + 드롭다운 */
  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-sub)]" />
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="pl-9"
          autoComplete="off"
        />
      </div>

      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-96 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
          {!debouncedQuery && results.length > 0 && (
            <div className="sticky top-0 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[10px] font-semibold uppercase text-[var(--color-text-sub)]">
              인기 종목
            </div>
          )}
          {/* 자동완성 결과 — 2행 레이아웃. 1행: 이름/코드/시장 배지, 2행: 현재가·등락률·PER·시총 요약 */}
          {results.map((t) => {
            const basic = t.basic ?? mockBasic(t.code, t.assetType);
            const changeCls = changeColorClass(basic.changePct);
            return (
              <button
                key={t.code}
                type="button"
                onClick={() => handleSelect(t)}
                className="block w-full px-3 py-2.5 text-left hover:bg-[var(--color-primary)]/10"
              >
                {/* 1행 — 이름 굵게 + 코드(회색 mono) + 시장 배지 */}
                <div className="flex items-center gap-1.5">
                  <span className="min-w-0 flex-1 truncate font-semibold text-[var(--color-text)]">{t.name}</span>
                  <span className="shrink-0 rounded bg-[var(--color-bg)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text-sub)]">
                    {t.code}
                  </span>
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold',
                      marketBadgeClass(t.market),
                    )}
                  >
                    {t.market}
                  </span>
                  {t.nameEn && (
                    <span className="ml-auto shrink-0 truncate text-[10px] text-[var(--color-text-sub)]">
                      {t.nameEn}
                    </span>
                  )}
                </div>
                {/* 2행 — 요약 (가격/등락률/PER/시총) */}
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-[var(--color-text-sub)]">
                  <span className="inline-flex items-center gap-0.5">
                    <span>현재가</span>
                    <span className="font-medium text-[var(--color-text)] tabular-nums">
                      {formatTickerPrice(basic, t.assetType)}
                    </span>
                  </span>
                  <span className={cn('font-semibold tabular-nums', changeCls)}>
                    {basic.changePct > 0 ? '+' : ''}
                    {basic.changePct.toFixed(2)}%
                  </span>
                  {basic.per !== null && (
                    <span className="inline-flex items-center gap-0.5">
                      <span>PER</span>
                      <span className="font-medium text-[var(--color-text)] tabular-nums">
                        {basic.per.toFixed(1)}
                      </span>
                    </span>
                  )}
                  <span className="inline-flex items-center gap-0.5">
                    <span>시총</span>
                    <span className="font-medium text-[var(--color-text)] tabular-nums">
                      {basic.marketCap}
                    </span>
                  </span>
                </div>
              </button>
            );
          })}
          {results.length === 0 && debouncedQuery && (
            <div className="px-3 py-4 text-center text-xs text-[var(--color-text-sub)]">
              "{debouncedQuery}" 검색 결과가 없어요
            </div>
          )}
        </div>
      )}
    </div>
  );
}
